// The in-memory twin of B4-ITEM-TEMPLATES (contracts/api/item-templates.ts): a board's item
// templates and `POST tasks {template_id}`. The rules are the server's
// (services/api/src/domain/boards/item-templates.ts): a template is saved from a task of the
// board (what it takes: description is the server's alone; checklists as one, fields, tags, due
// and start as days after creation; assignees only when asked) or written outright; one live name
// per board, ignoring case (409); archived, never deleted; a task from a template takes what the
// body leaves out, and a field, status, seat or tag the board no longer has is left out.
// `fill` lays a template into a `tasks.create` body; fake.ts's `tasks.create` calls it first.
// `fake-item-templates-contract.test.ts` holds it to the contracts.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Ctx, type Handler, json, notFound, type Principal, peekJson, problem } from "./fake";
import { uid } from "./fake-data";
import type { WorkBridge } from "./fake-work";

type ItemTemplate = OperationOutput<"itemTemplates.list">["data"][number];
type Payload = ItemTemplate["payload"];

const DAY = 86_400_000;
const validation = (detail: string) =>
  problem(422, "validation_failed", "Validation failed", detail);

export function createItemTemplatesFake(deps: {
  now: () => Date;
  work: () => WorkBridge | null;
  /** The Work twin's own handlers (`tasks.get`). */
  workHandlers: () => Partial<Record<RouteId, Handler>>;
}): {
  handlers: Partial<Record<RouteId, Handler>>;
  fill: (ctx: Ctx) => { ctx: Ctx; after: (res: Response) => Response } | Response;
} {
  let seq = 0;
  const next = () => uid(0x7e47, ++seq);
  const stamp = () => deps.now().toISOString();
  const byCompany = new Map<string, ItemTemplate[]>();
  const all = (co: string) => {
    const l = byCompany.get(co) ?? [];
    byCompany.set(co, l);
    return l;
  };
  /** Client viewers see no work (as the Work twin). */
  const reader = (p: Principal | null): p is Principal =>
    p?.roles.some((r) => r !== "client_viewer") === true;
  const membershipOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
  const boardOf = (p: Principal, id: string | undefined) =>
    id ? (deps.work()?.boardOf(p.company.id, id) ?? null) : null;
  const live = (p: Principal, boardId: string) =>
    all(p.company.id)
      .filter((t) => t.board_id === boardId && !t.archived_at)
      .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  const fieldIds = (p: Principal, boardId: string) =>
    new Set(
      (deps.work()?.stateOf(p.company.id)?.fields ?? [])
        .filter((f) => f.board_id === boardId)
        .map((f) => f.id),
    );
  const taken = (p: Principal, boardId: string, name: string, except?: string) =>
    live(p, boardId).some((t) => t.id !== except && t.name.toLowerCase() === name.toLowerCase());

  function checkPayload(p: Principal, boardId: string, raw: unknown): Payload | Response {
    const b = (raw ?? {}) as Payload;
    const title = typeof b.title === "string" ? b.title.trim() : "";
    if (!title) return validation("payload.title: a template needs a task title");
    const board = boardOf(p, boardId);
    if (b.status !== undefined && !board?.status_set.some((s) => s.key === b.status))
      return validation(`payload.status: "${b.status}" is not a status of this board`);
    const ids = fieldIds(p, boardId);
    for (const id of Object.keys(b.fields ?? {}))
      if (!ids.has(id)) return validation(`payload.fields.${id}: not a field of this board`);
    return { ...b, title };
  }

  /** A task of the board as a payload (the server's `payloadFromTask`, the description aside). */
  function fromTask(
    p: Principal,
    boardId: string,
    taskId: string,
    inc: Record<string, boolean | undefined>,
  ): Payload | Response {
    const t = deps.work()?.taskOf(p.company.id, taskId);
    if (!t || t.board_id !== boardId) return validation("from_task_id: no such task on this board");
    if ((t as { restricted?: boolean }).restricted)
      return validation("from_task_id: a restricted task cannot be saved as a template");
    const on = (k: string, dflt = true) => inc[k] ?? dflt;
    const status = boardOf(p, boardId)?.status_set.find((s) => s.key === t.status);
    const out: Payload = {
      title: t.title,
      ...(status && !status.done ? { status: status.key } : {}),
      ...(t.priority ? { priority: t.priority } : {}),
      ...(t.estimate_minutes !== null ? { estimate_minutes: t.estimate_minutes } : {}),
    };
    if (on("checklists") && t.checklist_items.length)
      out.checklist = t.checklist_items.map((i) => i.text);
    if (on("fields") && Object.keys(t.fields).length) out.fields = { ...t.fields };
    if (on("assignees", false) && t.assignee_seat_ids.length)
      out.assignee_seat_ids = [...t.assignee_seat_ids].sort();
    if (on("tags") && t.tag_ids.length) out.tag_ids = [...t.tag_ids].sort();
    if (on("dates")) {
      const days = (to: string | null) =>
        to ? Math.max(0, Math.round((Date.parse(to) - Date.parse(t.created_at)) / DAY)) : undefined;
      const due = days(t.due_at);
      const start = days(t.start_at);
      if (due !== undefined) out.due_in_days = due;
      if (start !== undefined) out.start_in_days = due !== undefined ? Math.min(start, due) : start;
    }
    return out;
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "itemTemplates.list": ({ principal: p, params }) => {
      if (!reader(p) || !boardOf(p, params.id)) return notFound();
      return json({ data: live(p, params.id as string) });
    },
    "itemTemplates.create": ({ principal: p, params, body }) => {
      if (!reader(p)) return notFound();
      const board = boardOf(p, params.id);
      if (!board) return notFound();
      if (board.archived_at) return problem(409, "conflict", "Conflict", "the board is archived");
      const b = (body ?? {}) as {
        name?: string;
        description?: string | null;
        payload?: unknown;
        from_task_id?: string;
        include?: Record<string, boolean | undefined>;
      };
      const name = (b.name ?? "").trim();
      if (!name) return validation("name: a template needs a name");
      if ((b.payload === undefined) === (b.from_task_id === undefined))
        return validation("give exactly one of payload and from_task_id");
      const raw = b.from_task_id
        ? fromTask(p, board.id, b.from_task_id, b.include ?? {})
        : b.payload;
      if (raw instanceof Response) return raw;
      const payload = checkPayload(p, board.id, raw);
      if (payload instanceof Response) return payload;
      if (taken(p, board.id, name))
        return problem(409, "conflict", "Conflict", `the board has a template named "${name}"`);
      const at = stamp();
      const t: ItemTemplate = {
        company_id: p.company.id,
        id: next(),
        board_id: board.id,
        name,
        description: b.description?.trim() || null,
        payload,
        source_task_id: b.from_task_id ?? null,
        position: live(p, board.id).length,
        archived_at: null,
        version: 1,
        actor_kind: "person",
        actor_membership_id: membershipOf(p),
        created_at: at,
        updated_at: at,
      };
      all(p.company.id).push(t);
      return json(t, 201);
    },
    "itemTemplates.update": ({ principal: p, params, body }) => {
      if (!reader(p)) return notFound();
      const t = live(p, params.id as string).find((x) => x.id === params.sub_id);
      if (!t) return notFound();
      const b = (body ?? {}) as {
        name?: string;
        description?: string | null;
        payload?: unknown;
        position?: number;
      };
      const name = b.name === undefined ? t.name : b.name.trim();
      if (!name) return validation("name: a template needs a name");
      if (name.toLowerCase() !== t.name.toLowerCase() && taken(p, t.board_id, name, t.id))
        return problem(409, "conflict", "Conflict", `the board has a template named "${name}"`);
      let payload = t.payload;
      if (b.payload !== undefined) {
        const checked = checkPayload(p, t.board_id, b.payload);
        if (checked instanceof Response) return checked;
        payload = checked;
      }
      const description =
        b.description === undefined ? t.description : b.description?.trim() || null;
      const changed =
        name !== t.name ||
        description !== t.description ||
        JSON.stringify(payload) !== JSON.stringify(t.payload) ||
        (b.position !== undefined && b.position !== t.position);
      if (!changed) return json(t);
      Object.assign(t, {
        name,
        description,
        payload,
        ...(b.position !== undefined ? { position: b.position } : {}),
        version: t.version + 1,
        updated_at: stamp(),
      });
      return json(t);
    },
    "itemTemplates.archive": ({ principal: p, params }) => {
      if (!reader(p)) return notFound();
      const t = live(p, params.id as string).find((x) => x.id === params.sub_id);
      if (!t) return notFound();
      t.archived_at = stamp();
      t.version += 1;
      return new Response(null, { status: 204 });
    },
  };

  /**
   * `POST tasks {template_id}` (fake.ts's `tasks.create` calls this first): the request with the
   * template laid into its body, and what to do with the made task's answer (mark it `template`,
   * put the template's tags on it). Without a template: the request as it is. A refusal: its 422.
   */
  function fill(ctx: Ctx): { ctx: Ctx; after: (res: Response) => Response } | Response {
    const same = { ctx, after: (res: Response) => res };
    const b = (ctx.body ?? {}) as Record<string, unknown>;
    const p = ctx.principal;
    if (typeof b.template_id !== "string" || !reader(p)) return same;
    {
      const { template_id, ...rest } = b;
      const t = live(p, String(b.board_id)).find((x) => x.id === template_id);
      if (!t) return validation("template_id: not a template of this board");
      const tp = t.payload;
      const board = boardOf(p, String(b.board_id));
      const fields = fieldIds(p, String(b.board_id));
      const kept = Object.fromEntries(
        Object.entries(tp.fields ?? {}).filter(([id]) => fields.has(id)),
      );
      const at = (d: number | undefined) =>
        d === undefined ? undefined : new Date(deps.now().getTime() + d * DAY).toISOString();
      const filled: Record<string, unknown> = {
        ...rest,
        title: rest.title ?? tp.title,
        ...(rest.status === undefined && board?.status_set.some((s) => s.key === tp.status)
          ? { status: tp.status }
          : {}),
        ...(rest.priority === undefined && tp.priority !== undefined
          ? { priority: tp.priority }
          : {}),
        ...(rest.estimate_minutes === undefined && tp.estimate_minutes !== undefined
          ? { estimate_minutes: tp.estimate_minutes }
          : {}),
        ...(rest.assignee_seat_ids === undefined && tp.assignee_seat_ids?.length
          ? { assignee_seat_ids: tp.assignee_seat_ids }
          : {}),
        ...(Object.keys(kept).length || rest.fields
          ? { fields: { ...kept, ...((rest.fields as Record<string, unknown>) ?? {}) } }
          : {}),
        ...(rest.checklist === undefined && tp.checklist?.length
          ? { checklist: tp.checklist }
          : {}),
        ...(rest.due_at === undefined && tp.due_in_days !== undefined
          ? { due_at: at(tp.due_in_days) }
          : {}),
        ...(rest.start_at === undefined && tp.start_in_days !== undefined
          ? { start_at: at(tp.start_in_days) }
          : {}),
        ...(rest.body === undefined && rest.body_markdown === undefined && tp.body?.length
          ? { body: tp.body }
          : {}),
      };
      const after = (res: Response) => {
        const made = peekJson<{ id: string }>(res);
        if (!made) return res;
        const task = deps.work()?.taskOf(p.company.id, made.id);
        if (task && task.origin === "manual") {
          task.origin = "template";
          task.origin_ref = { item_template_id: t.id };
          task.tag_ids = [...new Set([...task.tag_ids, ...(tp.tag_ids ?? [])])];
        }
        const get = deps.workHandlers()["tasks.get"];
        const detail = get
          ? peekJson(get({ ...ctx, params: { ...ctx.params, id: made.id } }))
          : null;
        return detail ? json(detail, 201) : res;
      };
      return { ctx: { ...ctx, body: filled }, after };
    }
  }

  return { handlers, fill };
}
